#!/usr/bin/env node
import express from 'express';
import { randomUUID } from 'crypto';
import { StreamableHTTPServerTransport } from '@modelcontextprotocol/sdk/server/streamableHttp.js';
import { SSEServerTransport } from '@modelcontextprotocol/sdk/server/sse.js';
import { createMcpServer, initializeMcpServer } from './core/server.js';
import { isInitializeRequest } from '@modelcontextprotocol/sdk/types.js';
import logger from './utils/logger.js';
import { tr } from 'zod/v4/locales';

// Environment variables
const PORT = process.env.PORT || 3000;
const ENABLE_LEGACY_SSE = true; // Enable legacy SSE support by default

/**
 * Main entry point for the HTTP transport version of the MCP server
 */
async function main() {
  try {
    logger.info('Starting Artillery Performance MCP Server with HTTP transport');

    const app = express();
    app.use(express.json());

    // Map to store transports by session ID
    const streamableTransports: Record<string, StreamableHTTPServerTransport> = {};
    const sseTransports: Record<string, SSEServerTransport> = {};

    // Handle POST requests for client-to-server communication (Streamable HTTP)
    app.post('/mcp', async (req, res) => {
      // Check for existing session ID
      const sessionId = req.headers['mcp-session-id'] as string | undefined;

      if (sessionId && streamableTransports[sessionId]) {
        // Reuse existing transport
        const transport = streamableTransports[sessionId];
        await transport.handleRequest(req, res, req.body);
      } else if (!sessionId && isInitializeRequest(req.body)) {
        // New initialization request
        const transport = new StreamableHTTPServerTransport({
          sessionIdGenerator: () => randomUUID(),
          onsessioninitialized: (sessionId) => {
            // Store the transport by session ID when it's initialized
            streamableTransports[sessionId] = transport;
            logger.info(`New session created: ${sessionId}`);
          }
        });

        // Set up transport handlers
        transport.onclose = () => {
          if (transport.sessionId) {
            delete streamableTransports[transport.sessionId];
            logger.info(`Session closed: ${transport.sessionId}`);
          }
        };

        // Create a new MCP server for this session
        const server = createMcpServer();
        await initializeMcpServer(server);

        // Connect transport to server
        await server.connect(transport);

        // Handle the initial request
        await transport.handleRequest(req, res, req.body);
      } else {
        // Invalid request
        res.status(400).json({
          jsonrpc: '2.0',
          id: null,
          error: {
            code: -32000,
            message: 'Invalid request or missing session ID'
          }
        });
      }
    });

    // Reusable handler for GET and DELETE streamable HTTP requests
    const handleSessionRequest = async (req: express.Request, res: express.Response) => {
      const sessionId = req.headers['mcp-session-id'] as string | undefined;

      if (!sessionId || !streamableTransports[sessionId]) {
        res.status(400).send('Invalid or missing session ID');
        return;
      }

      const transport = streamableTransports[sessionId];
      await transport.handleRequest(req, res);
    };

    // Handle GET requests for server-to-client notifications via Streamable HTTP
    app.get('/mcp', handleSessionRequest);

    // Handle DELETE requests for session termination
    app.delete('/mcp', handleSessionRequest);

    // Legacy SSE support if enabled
    if (ENABLE_LEGACY_SSE) {
      logger.info('Legacy SSE transport support is enabled');
      // Generate a unique session ID
      const sessionId = randomUUID();
      // Handle GET requests for SSE connections
      app.get('/sse', async (req, res) => {

        // Set up SSE headers
        res.setHeader('Content-Type', 'text/event-stream');
        res.setHeader('Cache-Control', 'no-cache');
        res.setHeader('Connection', 'keep-alive');

        // Create SSE transport
        const transport = new SSEServerTransport('/messages', res);
        logger.info(`New SSE session created: ${sessionId}`);
        sseTransports[sessionId] = transport;

        // Create a new MCP server for this session
        const server = createMcpServer();
        await initializeMcpServer(server);

        // Connect transport to server
        await server.connect(transport);

        // Remove transport when connection closes
        res.on('close', () => {
          delete sseTransports[sessionId];
          logger.info(`SSE session closed: ${sessionId}`);
          server.close();
        });

        // Send a welcome message on SSE session initialization
        res.write(`data: ${JSON.stringify({ message: 'SSE session initialized', sessionId })}\n\n`);

        // Send session ID as first event
        res.write(`data: ${JSON.stringify({ sessionId })}\n\n`);
      });

      // Handle POST messages for SSE connections
      app.post('/messages', async (req, res) => {

        if (!sessionId || !sseTransports[sessionId]) {
          res.status(400).json({
            error: 'Invalid or missing session ID'
          });
          return;
        }

        const transport = sseTransports[sessionId];
        await transport.handlePostMessage(req, res, req.body);
      });
    }

    // Basic health check endpoint
    app.get('/health', (req, res) => {
      res.status(200).json({
        status: 'healthy',
        version: '1.0.0'
      });
    });

    // Start the server
    app.listen(PORT, () => {
      logger.info(`Artillery Performance MCP Server listening on port ${PORT}`);
      logger.info(`Streamable HTTP endpoint available at: http://localhost:${PORT}/mcp`);

      if (ENABLE_LEGACY_SSE) {
        logger.info(`Legacy SSE endpoint available at: http://localhost:${PORT}/sse`);
      }
    });
  } catch (error) {
    logger.error(`Error starting MCP server: ${error}`);
    process.exit(1);
  }
}

// Start the server
main().catch(error => {
  logger.error(`Unhandled error: ${error}`);
  process.exit(1);
});
